import { NgZone } from "@angular/core";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import EsriSymbol from "@arcgis/core/symbols/Symbol";
import MapView from "@arcgis/core/views/MapView";
import type { SketchProperties } from "@arcgis/core/widgets/Sketch";
import type { SketchViewModelProperties } from "@arcgis/core/widgets/Sketch/SketchViewModel";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import type { State, UpdateEvent, UpdateOptions } from "@arcgis/core/widgets/Sketch/types";
import { Subject } from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { MapPopupService } from "./components/widgets/map-popup/map-popup.service";
import { differenceOperator, simplifyOGCOperator, unionOperator } from "./geometry-operators";
import { MapComponent } from "./map.component";
import { IMapSketch } from "./map.models";
import { IMapDrawLayer, IMapGeometry, ISketchParams, SketchToolType } from "./map.settings";
import { MapUtilsService } from "./services/map-utils.service";
import { SpatialService } from "./services/spatial.service";
import { SymbolService } from "./services/symbol.service";

type esriGeographyType = "point" | "multipoint" | "polyline" | "polygon" | "extent" | "mesh"; // Samma som __esri.Geography.type

export class MapSketch implements IMapSketch {

    sketchViewModel: SketchViewModel;
    map: MapComponent;
    get widgetReady(): boolean {
        return !!this.sketchViewModel;
    }
    get sketchState(): State {
        return this.sketchViewModel?.state ?? "disabled";
    }
    onStateChange = new Subject<string>();
    private drawLayer: IMapDrawLayer;
    private events: ResourceHandle[] = [];
    private throttleSignalUpdate = 0;
    private abortCallback?: () => void;
    private readonly defaultUpdateOptions = {
        tool: "reshape",
        enableRotation: false,
        enableScaling: false,
        multipleSelectionEnabled: false,
        toggleToolOnClick: false,
        reshapeOptions: {
            edgeOperation: "split",
            shapeOperation: "none",
            vertexOperation: "move"
        }
    } as UpdateOptions;
    private defaultTargetSymbol: EsriSymbol;
    private target: Graphic;
    private graphicsLayers: GraphicsLayer[];
    private view: MapView;

    constructor(
        map: MapComponent,
        private readonly symbolService: SymbolService,
        private readonly mapPopupService: MapPopupService,
        private readonly spatialService: SpatialService,
        private readonly mapUtilsService: MapUtilsService,
        private readonly ngZone: NgZone) {
        this.map = map;
        this.view = map.view;

    }

    init(graphicsLayers: GraphicsLayer[], view: MapView): void {
        this.graphicsLayers = graphicsLayers;
        this.view = view;
    }

    sketch(layer: IMapDrawLayer, tool: SketchToolType, onCreateCallback?: (g) => void): void {
        this.startSketch({
            drawLayer: layer,
            tools: [tool],
            activeTool: tool
        });
        if (onCreateCallback) {
            this.events.push(this.sketchViewModel.on("create", (event) => this.signalCreate(event.state, [event.graphic], onCreateCallback)));
        }
    }

    onAbort(callback?: () => void) {
        this.abortCallback = callback;
    }

    edit(layer: IMapDrawLayer, target: IMapGeometry, callback?: (g) => void, disableMove = false): void {

        if (ObjectUtils.isNullOrUndefined(target)) {
            return;
        }
        const arcGraphic = this.getArcGraphic(target);
        arcGraphic.geometry = simplifyOGCOperator.execute(arcGraphic.geometry);
        const editParams = {
            drawLayer: layer,
            updateOptions: this.getUpdateOptions(null, [arcGraphic.geometry.type])
        } as ISketchParams;

        this.startSketch(editParams, () => {
            this.target = arcGraphic;
            this.defaultTargetSymbol = arcGraphic.symbol;
            this.sketchViewModel.update([arcGraphic]);
            let changed = false;
            let ignoreNextEvent = false;

            const editEventListener = this.sketchViewModel.on("update", (e) => {

                if (ignoreNextEvent) {
                    ignoreNextEvent = false;
                    return;
                }

                switch (e.state) {
                    case "start":
                        this.symbolService.setSymbol(arcGraphic, this.getEditSymbol(arcGraphic.geometry.type));
                        break;
                    case "active": {
                        const { changeState, ignoreEvent } = this.sketchActiveState(e, arcGraphic, disableMove);
                        changed = changeState;
                        ignoreNextEvent = ignoreEvent;
                        break;
                    }
                    case "complete":
                        this.sketchCompleteState(e, changed, editEventListener, callback);
                        break;
                    default:
                        console.error(`unhandled state`, e.state);
                        break;
                }
            });
        });
    }

    add(layer: IMapDrawLayer, target: IMapGeometry, callback?: (g) => void): void {
        this.combineWithNewPolygon(layer, target, unionOperator.execute, callback);
    }

    cut(layer: IMapDrawLayer, target: IMapGeometry, callback?: (g) => void): void {
        this.combineWithNewPolygon(layer, target, differenceOperator.execute, callback);
    }

    startSketch(params: ISketchParams, callback?: () => void): SketchViewModel {
        this.mapPopupService.block();
        this.stopSketch();
        this.drawLayer = params.drawLayer;
        const graphicsLayer = this.graphicsLayers.find(x => x.id === this.drawLayer.id);
        const viewModel = this.initNewSketch(params, graphicsLayer);

        this.events.push(this.sketchViewModel.on("create", (event) => this.signalCreate(
            event.state,
            [event.graphic],
            this.drawLayer.onGeometryCreated,
            this.drawLayer.onGeometryCanceled)));
        this.events.push(this.sketchViewModel.on("update", (event) => this.signalUpdate(event, event.graphics, this.drawLayer.onGeometryChanged)));
        this.events.push(this.sketchViewModel.on("delete", (event) => this.signalDelete(event.graphics, this.drawLayer.onGeometryDeleted)));
        this.events.push(reactiveUtils.watch(() => this.sketchViewModel.state, (event) => this.signalStateChange(event)));
        this.ngZone.runOutsideAngular(() => {
            window.requestAnimationFrame(() => {
                if (callback) {
                    callback();
                }
                if (params.activeTool) {
                    this.sketchViewModel.create(params.activeTool as any);
                }
            });
        });

        const properties = {
            view: this.view,
            viewModel: viewModel,
            id: "sketch",
            creationMode: "single",
            layer: graphicsLayer,
            availableCreateTools: params.tools ? params.tools : this.drawLayer.geometryTypes,
            defaultCreateOptions: params.createOptions ? params.createOptions : { mode: "click" },
            defaultUpdateOptions: params.updateOptions ? params.updateOptions : this.defaultUpdateOptions
        } as SketchProperties;

        const drawoptions = this.drawLayer?.drawOptions;
        if (drawoptions?.length && drawoptions[0]?.snapLayerId) {
            const snapLayer = this.getLayer(drawoptions[0]?.snapLayerId);
            properties.snappingOptions = {
                enabled: true,
                featureSources: [
                    {
                        layer: snapLayer,
                        enabled: true
                    }
                ],
                distance: 5
            };
        }

        return viewModel;
    }

    updateSketchWidget(layerId?: string): void {
        if (layerId && this.drawLayer?.id !== layerId) {
            return;
        }
        this.sketchViewModel.polygonSymbol = this.getEditSymbol("polygon") as SimpleFillSymbol;
        this.sketchViewModel.pointSymbol = this.getEditSymbol("point") as SimpleMarkerSymbol;
        this.sketchViewModel.polylineSymbol = this.getEditSymbol("polyline") as SimpleLineSymbol;
    }

    complete(): void {
        this.resetSymbolOnTarget();
        this.sketchViewModel.complete();
    }

    cancelSketch(layer?): void {
        if (!this.sketchViewModel || layer && layer !== this.drawLayer) {
            return;
        }

        this.resetSymbolOnTarget();
        this.sketchViewModel?.cancel();
        this.signalStateChange(false);
        this.sketchViewModel?.destroy();
        this.events.forEach(e => {
            e.remove();
        });
        this.events = [];
        this.drawLayer = undefined;
        this.sketchViewModel = undefined;
        this.mapPopupService.unblock();
    }

    getLayer(layerid: string): any {
        return this.view.layerViews.find(x => x.layer.id === layerid).layer;
    }

    private stopSketch(layer?): void {
        if (!this.sketchViewModel || layer && layer !== this.drawLayer) {
            return;
        }
        this.complete();
        this.signalStateChange(false);
        this.sketchViewModel?.destroy();
        this.events.forEach(e => {
            e.remove();
        });
        this.events = [];
        this.drawLayer = undefined;
        this.sketchViewModel = undefined;
        this.mapPopupService.unblock();
    }

    private sketchActiveState(e: UpdateEvent, arcGraphic: Graphic, disableMove: boolean): { changeState: boolean; ignoreEvent: boolean } {
        if (disableMove && e.toolEventInfo?.type === "move-stop") {
            // this.sketchViewModel.cancel() - orsakar ett fel internt i arcis, därav denna lösning
            this.sketchViewModel.undo();
            return {
                changeState: false,
                ignoreEvent: true
            };
        }
        return {
            changeState: true,
            ignoreEvent: false
        };
    }

    private sketchCompleteState(e: UpdateEvent, changed: boolean, editEventListener: ResourceHandle, callback?: (g) => void): void {
        if (e.aborted) {
            if (this.abortCallback) {
                this.abortCallback();
            } else {
                console.error("Abort not possible due to MapSketchService missing a registered abortCallback");
            }
        } else if (callback && changed) {
            const graphic = e.graphics[0];
            this.spatialService.verifyGeoId(graphic);
            graphic.geometry = simplifyOGCOperator.execute(graphic.geometry);
            callback(this.spatialService.toRask(graphic));
        }
        editEventListener.remove();
        this.stopSketch();
    }

    private combineWithNewPolygon(layer: IMapDrawLayer, target: IMapGeometry, operation: (targetGeometry: any, newGeometry: any) => any, callback?: (g) => void): void {
        this.startSketch({
            drawLayer: layer,
            tools: ["polygon"],
            activeTool: "polygon"
        });
        const createEvent = this.sketchViewModel.on("create", (event) => {
            if (event.state !== "complete") {
                return;
            }
            const targetGraphic = this.getArcGraphic(target);
            const newGeometry = event.graphic.geometry;
            if (callback) {
                targetGraphic.geometry = operation(targetGraphic.geometry, newGeometry);
                this.getArcLayer(layer).remove(event.graphic);
                callback(this.spatialService.toRask(targetGraphic));
                createEvent.remove();
                this.stopSketch();
            }
        });
    }

    private getArcLayer(raskLayer?: IMapDrawLayer) {
        if (!raskLayer) {
            raskLayer = this.drawLayer;
        }
        return this.graphicsLayers.find(l => l.id === raskLayer.id);
    }

    private getArcGraphic(raskGeometry: IMapGeometry): Graphic {
        const graphic = this.graphicsLayers.find(l => l.graphics?.find(i => i.attributes.id === raskGeometry.id))
            ?.graphics.find(i => i.attributes.id === raskGeometry.id);
        if (ObjectUtils.isNullOrUndefined(graphic)) {
            console.error("Graphic not found");
        }
        return graphic;
    }

    private initNewSketch(params: ISketchParams, graphicsLayer: GraphicsLayer): SketchViewModel {

        const updateOptions = this.getUpdateOptions(
            params.updateOptions,
            params.tools?.length ? params.tools : this.drawLayer?.geometryTypes
        );

        const properties = {
            view: this.view,
            layer: graphicsLayer,
            defaultCreateOptions: params.createOptions ? params.createOptions : { mode: "click" },
            defaultUpdateOptions: updateOptions
        } as SketchViewModelProperties;

        this.sketchViewModel = new SketchViewModel(properties);

        this.updateSketchWidget();
        return this.sketchViewModel;
    }

    private signalCreate(eventState: string, graphics: any[], callBackFn?: any, cancelCallbackFn?: any) {
        if (eventState === "complete") {
            this.stopSketch();
            graphics.forEach((graphic) => {
                graphic.setAttribute("geoid", this.spatialService.generateGeoId());
                if (callBackFn) {
                    callBackFn(this.spatialService.toRask(graphic), this.mapUtilsService.getScreenPosition(graphic.geometry, this.view));
                }
            });
        } else if (eventState === "cancel" && cancelCallbackFn) {
            cancelCallbackFn();
        }
    }

    private signalUpdate(event: UpdateEvent, graphics: any[], callBackFn?: any) {
        if (event.state === "complete" && callBackFn) {
            event.graphics.forEach((graphic) => callBackFn(this.spatialService.toRask(graphic)));
        } else if (event.state === "active" && callBackFn) {
            if (this.throttleSignalUpdate === 0) {
                setTimeout(() => {
                    event.graphics.forEach((graphic) => callBackFn(this.spatialService.toRask(graphic)));
                    this.throttleSignalUpdate = 0;
                }, 1000);
            }
            this.throttleSignalUpdate++;
        }
    }

    private signalDelete(graphics: any[], callBackFn?: any) {
        if (callBackFn) {
            callBackFn(graphics.map(g => this.spatialService.toRask(g)));
        }
    }

    private signalStateChange(event) {
        this.onStateChange.next(event);
        if (event === "ready" && this.drawLayer.onEditGeometryExit) {
            this.drawLayer.onEditGeometryExit();
            return;
        }
        if (event === "active" && this.drawLayer.onEditGeometryEnter) {
            const graphics = this.sketchViewModel.updateGraphics;
            graphics.forEach((graphic) => {
                this.drawLayer.onEditGeometryEnter(this.spatialService.toRask(graphic));
            });
        }
    }

    private getEditSymbol(type: esriGeographyType): EsriSymbol {
        switch (type) {
            case "point":
                return this.symbolService.getSymbolByStyleName("editing");
            case "polyline":
                return this.symbolService.getSymbolByStyleName("editingLine");
            case "polygon":
                return this.symbolService.getSymbolByStyleName("editingPolygon");
            default:
                throw Error("Edit Symbol: Unknown geometry type.");
        }
    }

    private resetSymbolOnTarget() {
        if (this.target && this.defaultTargetSymbol) {
            this.target.set("symbol", this.defaultTargetSymbol);
            this.defaultTargetSymbol = null;
        }
    }

    // updateOptions - point: movable, polygon, polyline: not movable. - or default
    private getUpdateOptions(updateOptions?: UpdateOptions, geometryTypes?: string[]): UpdateOptions {
        if (updateOptions) {
            return updateOptions;
        }

        if (geometryTypes?.some(x => x === "polygon" || x === "polyline")) {
            return {
                tool: "reshape",
                toggleToolOnClick: false,
                reshapeOptions: {
                    shapeOperation: "none",
                    edgeOperation: "split"
                }
            };
        }

        if (geometryTypes?.every(x => x === "point")) {
            return {
                tool: "move",
                multipleSelectionEnabled: false,
                toggleToolOnClick: false
            };
        }

        return this.defaultUpdateOptions;
    }

}
